import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useChatMessages } from '../hooks/useMessages';
import CloudinaryImage from '../components/CloudinaryImage';
import { 
  ArrowLeft, 
  Send, 
  User,
  MessageCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function Chat() {
  const { chatId } = useParams();
  const { user, userType, landlordType } = useAuth();
  const navigate = useNavigate();
  const [chat, setChat] = useState(null);
  const [otherParticipant, setOtherParticipant] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  
  const { messages, loading, sending, sendMessage } = useChatMessages(chatId, user?.uid);
  
  // Utility functions
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleTimeString('nl-NL', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  const getOtherParticipant = (participants) => {
    if (!participants || !user) return null;
    const otherUid = Array.isArray(participants) 
      ? participants.find(p => p !== user.uid)
      : null;
    return otherUid;
  };

  useEffect(() => {
    if (chatId && user) {
      loadChat();
    }
  }, [chatId, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChat = async () => {
    try {
      console.log('=== LOADING CHAT ===');
      console.log('Chat ID from params:', chatId);
      console.log('Chat ID type:', typeof chatId);
      console.log('Chat ID length:', chatId?.length);
      console.log('User:', user?.uid);
      console.log('Current URL:', window.location.href);
      console.log('Pathname:', window.location.pathname);
      
      const fallbackMessagesPath = userType === 'landlord'
        ? (landlordType === 'agency' ? '/landlord/agency/inbox' : '/landlord/messages')
        : '/messages';

      if (!chatId || chatId.trim() === '') {
        console.log('❌ No chatId provided or chatId is empty');
        toast.error('Geen chat ID opgegeven in URL');
        navigate(fallbackMessagesPath);
        return;
      }
      
      if (!user) {
        console.log('No user logged in');
        toast.error('Niet ingelogd');
        navigate(fallbackMessagesPath);
        return;
      }
      
      console.log('Fetching chat document...');
      const chatDoc = await getDoc(doc(db, 'chats', chatId));
      console.log('Chat document exists:', chatDoc.exists());
      
      if (chatDoc.exists()) {
        const chatData = chatDoc.data();
        console.log('Chat data:', chatData);
        console.log('Chat participants:', chatData.participants);
        console.log('User is participant:', chatData.participants?.includes(user.uid));
        
        if (chatData.participants?.includes(user.uid)) {
          console.log('✅ User is participant, loading chat');
          setChat({ id: chatDoc.id, ...chatData });
          
          // Load other participant data
          const otherUid = chatData.participants.find(uid => uid !== user.uid);
          if (otherUid) {
            try {
              const otherUserDoc = await getDoc(doc(db, 'users', otherUid));
              if (otherUserDoc.exists()) {
                setOtherParticipant({
                  uid: otherUid,
                  ...otherUserDoc.data()
                });
              } else {
                setOtherParticipant({ uid: otherUid, displayName: 'Onbekende gebruiker' });
              }
            } catch (error) {
              console.error('Error loading other participant:', error);
              setOtherParticipant({ uid: otherUid, displayName: 'Onbekende gebruiker' });
            }
          }
        } else {
          console.log('❌ User is not a participant in this chat');
          toast.error('Je hebt geen toegang tot deze chat');
          navigate(fallbackMessagesPath);
        }
      } else {
        console.log('❌ Chat document does not exist');
        toast.error('Chat niet gevonden. Probeer opnieuw.');
        navigate(fallbackMessagesPath);
      }
    } catch (error) {
      console.error('Error loading chat:', error);
      toast.error('Fout bij het laden van de chat: ' + error.message);
      const fallbackMessagesPath = userType === 'landlord'
        ? (landlordType === 'agency' ? '/landlord/agency/inbox' : '/landlord/messages')
        : '/messages';
      navigate(fallbackMessagesPath);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || sending) return;
    
    try {
      await sendMessage(newMessage.trim());
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Fout bij het versturen van het bericht');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container-professional py-12">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ios-blue"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container-professional py-12">
          <div className="text-center py-20">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Chat niet gevonden</h2>
            <p className="text-gray-600 mb-4">Chat ID: {chatId}</p>
            <p className="text-gray-600 mb-4">User: {user?.uid}</p>
            <button
              onClick={() => navigate(userType === 'landlord'
                ? (landlordType === 'agency' ? '/landlord/agency/inbox' : '/landlord/messages')
                : '/messages')}
              className="btn-primary"
            >
              Terug naar berichten
            </button>
            <button
              onClick={() => navigate('/direct-chat-test')}
              className="btn-secondary ml-4"
            >
              Test Chat Direct
            </button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-professional py-12">
        {/* Chat Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
              {otherParticipant?.logoURL ? (
                <CloudinaryImage
                  publicId={otherParticipant.logoURL}
                  alt={otherParticipant.displayName || otherParticipant.companyName || 'Gebruiker'}
                  className="w-10 h-10 rounded-full"
                  width={40}
                  height={40}
                  type="avatar"
                />
              ) : (
                <User className="w-5 h-5 text-gray-400" />
              )}
            </div>
            
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">
                {otherParticipant?.displayName || 
                 otherParticipant?.companyName || 
                 otherParticipant?.email || 
                 'Onbekende gebruiker'}
              </h2>
              <p className="text-sm text-gray-500">
                {otherParticipant?.email || 'Geen email beschikbaar'}
              </p>
            </div>
          </div>
        </div>

        {/* Messages Container */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-96 flex flex-col">
          {/* Messages List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <MessageCircle className="w-12 h-12 mb-4" />
                <p>Nog geen berichten</p>
                <p className="text-sm">Stuur het eerste bericht!</p>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.senderId === user?.uid ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message.senderId === user?.uid
                        ? 'bg-ios-blue text-white'
                        : 'bg-gray-200 text-gray-900'
                    }`}
                  >
                    <p className="text-sm">{message.text}</p>
                    <p
                      className={`text-xs mt-1 ${
                        message.senderId === user?.uid
                          ? 'text-blue-100'
                          : 'text-gray-500'
                      }`}
                    >
                      {formatMessageTime(message.timestamp)}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <div className="border-t border-gray-200 p-4">
            <form onSubmit={handleSendMessage} className="flex space-x-3">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Typ je bericht..."
                className="flex-1 input-field"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {sending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}